import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

export const Hero = () => {
  const highlights = [
    'Exclusive Appointments',
    'No Shared Leads',
    'Pay Per Result',
  ];

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8fafc] to-transparent opacity-50" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Text Content */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-6">
              <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] animate-fade-in-up">
                Appointment Generation for Contractors
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-[#0f172a] tracking-tight leading-none animate-fade-in-up animation-delay-100">
                Real Appointments.<br />
                <span className="text-[#2563eb]">Real Growth.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#64748b] leading-relaxed max-w-xl animate-fade-in-up animation-delay-200">
                RenoNovo connects home service contractors with homeowners actively seeking their services. 
                Stop wasting money on clicks — only pay for qualified appointments.
              </p>
            </div>

            {/* Highlights */}
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm font-medium text-[#0f172a]">
                  <CheckCircle2 className="w-5 h-5 text-[#2563eb]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/book-call">
                <Button 
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-14 rounded-sm text-base shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_0_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5 group"
                  data-testid="hero-cta-primary"
                >
                  Book Free Strategy Call
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button 
                  variant="outline"
                  className="bg-white text-[#0f172a] font-semibold px-8 h-14 rounded-sm text-base border-2 border-[#0f172a]/10 hover:border-[#0f172a]/30 hover:bg-[#f8fafc] transition-all duration-200"
                  data-testid="hero-cta-secondary"
                >
                  See How It Works
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="pt-8 border-t border-[#e2e8f0] animate-fade-in-up animation-delay-500">
              <p className="text-sm text-[#64748b] mb-4">Trusted by 500+ home service contractors nationwide</p>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#0f172a]">50K+</p>
                  <p className="text-xs text-[#64748b] uppercase tracking-wide">Appointments</p>
                </div>
                <div className="w-px h-12 bg-[#e2e8f0]" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#0f172a]">$500M+</p>
                  <p className="text-xs text-[#64748b] uppercase tracking-wide">Revenue Generated</p>
                </div>
                <div className="w-px h-12 bg-[#e2e8f0]" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#0f172a]">5.0</p>
                  <p className="text-xs text-[#64748b] uppercase tracking-wide">Client Rating</p>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-5 relative animate-slide-in-right">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#2563eb]/10 rounded-sm transform rotate-3" />
              <img
                src="https://images.unsplash.com/photo-1760129745103-91c4022ed5fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Modern luxury home"
                className="relative w-full h-auto rounded-sm shadow-[0_12px_24px_rgba(15,23,42,0.12)]"
              />
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-[0_12px_24px_rgba(15,23,42,0.12)] border border-[#e2e8f0] animate-fade-in-up animation-delay-500">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#2563eb]/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#2563eb]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f172a]">87%</p>
                  <p className="text-sm text-[#64748b]">Conversion Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
